import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Login.css';

function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Check if email is @securelayer7.net
  const isSecureLayer7Email = email.toLowerCase().endsWith('@securelayer7.net');

  // Handle OAuth callback from Microsoft
  useEffect(() => {
    const sessionId = searchParams.get('sessionId');
    const oauthEmail = searchParams.get('email');
    const role = searchParams.get('role');
    const errorParam = searchParams.get('error');

    console.log('[Login] useEffect - URL params:', { 
      sessionId: sessionId ? sessionId.substring(0, 8) + '...' : null, 
      email: oauthEmail, 
      role, 
      error: errorParam 
    });

    if (errorParam) {
      const message = searchParams.get('message');
      console.error('[Login] SSO error:', errorParam, message);
      if (errorParam === 'invalid_state') {
        setError('Login session expired. Please try again.');
      } else if (errorParam === 'invalid_domain') {
        setError('Microsoft SSO is only available for @securelayer7.net emails.');
      } else {
        setError(`Authentication failed: ${message || errorParam}. Please try again.`);
      }
      return;
    }

    if (sessionId && oauthEmail && role) {
      console.log('[Login] SSO callback - storing session and setting user');

      // Mark SSO login timestamp so 401 interceptor doesn't auto-logout during grace period
      sessionStorage.setItem('ssoLoginTimestamp', Date.now().toString());

      // Store session and redirect
      const user = { email: oauthEmail, role };
      login(user, sessionId);
      
      console.log('[Login] SSO callback - localStorage sessionId:', localStorage.getItem('sessionId')?.substring(0, 8) + '...');

      // Use setTimeout to allow React state to settle before navigating
      setTimeout(() => {
        // Verify the session was stored correctly
        const storedSession = localStorage.getItem('sessionId');
        console.log('[Login] Pre-navigate check - sessionId in localStorage:', storedSession ? storedSession.substring(0, 8) + '...' : 'MISSING!');
        
        if (!storedSession) {
          console.error('[Login] SESSION LOST! Something cleared localStorage between login() and navigate. Restoring...');
          localStorage.setItem('sessionId', sessionId);
        }

        // Check for stored redirect URL first (user was trying to access a protected route)
        const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
        if (redirectUrl) {
          console.log('[Login] Redirecting to stored URL after OAuth:', redirectUrl);
          sessionStorage.removeItem('redirectAfterLogin');
          navigate(redirectUrl, { replace: true });
          return;
        }

        // No stored URL, redirect based on role
        console.log('[Login] Navigating based on role:', role);
        switch (role) {
          case 'admin':
            navigate('/admin', { replace: true });
            break;
          case 'trainer':
            navigate('/training', { replace: true });
            break;
          case 'interviewer':
            navigate('/interviewer', { replace: true });
            break;
          case 'recruiter':
            navigate('/recruiter', { replace: true });
            break;
          case 'candidate':
            navigate('/candidate', { replace: true });
            break;
          default:
            navigate('/', { replace: true });
        }
      }, 200); // Delay to allow React state to settle
    }
  }, [searchParams]); // Minimal dependencies to prevent re-runs

  // Handle Microsoft SSO login
  const handleMicrosoftLogin = () => {
    setLoading(true);
    // Redirect to backend MS SSO endpoint
    window.location.href = `/api/auth/microsoft/login?email=${encodeURIComponent(email)}&returnUrl=/login`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Send magic link to email
      const response = await fetch('/api/auth/request-magic-link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Magic link sent! Please check your email to complete login.');
        setEmail(''); // Clear email field
      } else {
        const errorMsg = typeof data.error === 'string'
          ? data.error
          : data.error?.message || 'Failed to send magic link. Please try again.';
        setError(errorMsg);
      }
    } catch (err) {
      setError('Unable to connect to server. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>SecHire</h1>
          <p>Security Assessment Platform</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              {success}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              required
              disabled={loading}
              autoFocus
            />
          </div>

          {/* Show Microsoft SSO button for @securelayer7.net emails */}
          {isSecureLayer7Email && email ? (
            <button
              type="button"
              className="btn btn-microsoft"
              onClick={handleMicrosoftLogin}
              disabled={loading}
            >
              {loading ? (
                'Redirecting to Microsoft...'
              ) : (
                <>
                  <svg className="microsoft-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 23 23">
                    <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
                    <path fill="#f35325" d="M1 1h10v10H1z"/>
                    <path fill="#81bc06" d="M12 1h10v10H12z"/>
                    <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                    <path fill="#ffba08" d="M12 12h10v10H12z"/>
                  </svg>
                  Sign in with Microsoft
                </>
              )}
            </button>
          ) : (
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !email}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          )}

          {/* Show helper text for non-securelayer7.net emails */}
          {email && !isSecureLayer7Email && (
            <div className="login-info">
              <p>A magic link will be sent to your email</p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default Login;
